import { useState } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard,
  Menu,
  Search,
  Grid3X3,
  Heart,
  MessageCircle,
  HelpCircle,
  Phone,
  X,
  LogIn,
  UserPlus,
} from "lucide-react";
import { MobileBottomNav } from "./MobileBottomNav";
import logoUrgSalud from "@/assets/logo-urgsalud-transparent.png";

const publicNavItems = [
  { label: "Inicio", href: "/", icon: LayoutDashboard },
  { label: "Áreas de Servicio", href: "/solicitar", icon: Grid3X3 },
  { label: "Buscar Profesionales", href: "/buscar-profesionales", icon: Search },
  { label: "Comunidad", href: "/comunidad", icon: MessageCircle },
  { label: "Preguntas Frecuentes", href: "/faq", icon: HelpCircle },
];

const mobileNavItems = [
  { label: "Inicio", href: "/", icon: LayoutDashboard },
  { label: "Áreas", href: "/solicitar", icon: Grid3X3 },
  { label: "Buscar", href: "/buscar-profesionales", icon: Search },
  { label: "Comunidad", href: "/comunidad", icon: MessageCircle },
  { label: "FAQ", href: "/faq", icon: HelpCircle },
];

export default function PublicPatientLayout({ children }: { children: React.ReactNode }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // If user is logged in, redirect to full patient layout
  // (handled by route, this is just the layout)

  const currentPageTitle =
    publicNavItems.find((item) => item.href === location.pathname)?.label || "Explorar";

  return (
    <div className="min-h-dvh bg-background">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed right-0 top-0 z-50 h-full w-64 bg-gradient-to-b from-primary to-primary/90 transform transition-transform duration-300 lg:translate-x-0 shadow-xl",
          sidebarOpen ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <Link to="/"><img src={logoUrgSalud} alt="UrgSalud Chile" className="h-9 w-auto brightness-0 invert" width={36} height={36} /></Link>
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(false)} className="lg:hidden text-white/70 hover:text-white hover:bg-white/10">
              <X className="w-5 h-5" />
            </Button>
          </div>

          <nav className="flex-1 overflow-y-auto py-4 px-3">
            <ul className="space-y-1">
              {publicNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <li key={item.href}>
                    <NavLink
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-all",
                        isActive ? "bg-white text-primary shadow-md" : "text-white/80 hover:text-white hover:bg-white/10",
                      )}
                    >
                      <Icon className="w-5 h-5" />
                      {item.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Emergency Notice */}
          <div className="px-4 py-3 mx-3 mb-3 rounded-lg bg-white/10 border border-white/20">
            <div className="flex items-center gap-2 text-xs text-white/90">
              <Phone className="w-4 h-4 text-accent flex-shrink-0" />
              <div>
                <p className="font-medium">Emergencias vitales</p>
                <p className="text-white/70">Llama al 131 (SAMU)</p>
              </div>
            </div>
          </div>

          {/* Auth buttons at bottom */}
          {!user && (
            <div className="border-t border-white/10 p-4 space-y-2">
              <Button
                onClick={() => navigate("/auth?mode=register")}
                className="w-full gap-2 bg-white text-primary hover:bg-white/90"
              >
                <UserPlus className="w-4 h-4" />
                Crear Cuenta
              </Button>
              <Button
                onClick={() => navigate("/auth")}
                variant="ghost"
                className="w-full gap-2 text-white/80 hover:text-white hover:bg-white/10"
              >
                <LogIn className="w-4 h-4" />
                Iniciar Sesión
              </Button>
            </div>
          )}
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pr-64">
        <header className="sticky top-0 z-30 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="flex items-center justify-between px-4 py-3 lg:px-6">
            <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} className="lg:hidden">
              <Menu className="w-5 h-5" />
            </Button>

            <div className="hidden lg:flex items-center gap-3">
              <h1 className="text-lg font-semibold text-foreground">{currentPageTitle}</h1>
            </div>

            <Link to="/" className="lg:hidden">
              <img src={logoUrgSalud} alt="UrgSalud" className="h-8 w-auto" width={32} height={32} />
            </Link>

            {!user && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate("/auth")} className="gap-1.5 text-xs">
                  <LogIn className="w-3.5 h-3.5" />
                  Ingresar
                </Button>
                <Button size="sm" onClick={() => navigate("/auth?mode=register")} className="gap-1.5 text-xs hidden sm:flex">
                  <UserPlus className="w-3.5 h-3.5" />
                  Crear Cuenta
                </Button>
              </div>
            )}
          </div>
        </header>

        <main className="p-4 lg:p-6 pb-24 lg:pb-6">{children}</main>
      </div>

      <MobileBottomNav items={mobileNavItems} activeColor="text-primary" />
    </div>
  );
}
